// Smart Links — Custom Domain panel.
import { BRAND_NAME } from '../../theme/brand';
//
// Mirrors the UX shape of App/src/components/CustomDomainsPanel.jsx (Stories)
// but trimmed for the per-spec "one custom domain per tenant" constraint and
// rewritten in Chakra (the App's primary UI kit) instead of Lucide + Tailwind.
//
// Backend endpoints (Smart Links API, see src/routes/domains.js):
//   GET    /domains                            current row or null
//   POST   /domains            {hostname}      add or fetch (idempotent)
//   GET    /domains/:cfId/status               poll Cloudflare for fresh state
//   POST   /domains/:cfId/activate {hostname}  flip to active once SSL ready
//   DELETE /domains/:cfId        {hostname}    remove from CF + soft-delete row

import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Flex, HStack, VStack, Heading, Text, Button, Input, Spinner, IconButton,
    Table, Thead, Tbody, Tr, Th, Td, Badge, useToast, Code, Alert, AlertIcon,
} from '@chakra-ui/react';
import { FiCopy, FiPlus, FiRefreshCw, FiTrash2, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

const SMARTLINKS_API = process.env.REACT_APP_SMARTLINKS_API_URL || 'http://localhost:4001';
const HOSTNAME_RE = /^(?=.{1,253}$)(?!-)[a-z0-9-]+(\.[a-z0-9-]+)+$/;

function api(method, path, body) {
    const headers = {
        'Content-Type': 'application/json',
        'x-api-key': localStorage.getItem('api-key') || '',
    };
    const opts = { method, headers };
    if (body) opts.body = JSON.stringify(body);
    return fetch(`${SMARTLINKS_API}${path}`, opts).then(async (r) => {
        const txt = await r.text();
        let data;
        try { data = txt ? JSON.parse(txt) : null; } catch { data = { raw: txt }; }
        if (!r.ok) {
            const err = new Error(data?.detail || data?.error || `HTTP ${r.status}`);
            err.status = r.status;
            err.body = data;
            throw err;
        }
        return data;
    });
}

function StatusPill({ status, sslStatus }) {
    if (status === 'active') return <Badge bg="#dcfce7" color="#166534">Active</Badge>;
    if (status === 'verifying') return <Badge bg="#fef3c7" color="#92400e">Verifying · {sslStatus || 'pending DNS'}</Badge>;
    if (status === 'suspended') return <Badge bg="#fee2e2" color="#991b1b">Suspended</Badge>;
    if (status === 'pending') return <Badge bg="var(--brand-100)" color="var(--brand-800)">Pending</Badge>;
    return <Badge bg="#e2e8f0" color="#475569">{status || '—'}</Badge>;
}

function DnsRecordsTable({ records, onCopy }) {
    if (!records || records.length === 0) {
        return <Text fontSize="sm" color="gray.500">DNS records will appear here once Cloudflare provisions them.</Text>;
    }
    return (
        <Table size="sm">
            <Thead bg="gray.50">
                <Tr>
                    <Th fontSize="11px" fontWeight={600} color="black.600">Type</Th>
                    <Th fontSize="11px" fontWeight={600} color="black.600">Name</Th>
                    <Th fontSize="11px" fontWeight={600} color="black.600">Value</Th>
                    <Th fontSize="11px" fontWeight={600} color="black.600" w="60px">Copy</Th>
                </Tr>
            </Thead>
            <Tbody>
                {records.map((r, i) => (
                    <Tr key={`${r.type}-${r.name}-${i}`}>
                        <Td><Badge bg="var(--brand-100)" color="var(--brand-800)">{r.type}</Badge></Td>
                        <Td><Code fontSize="xs" wordBreak="break-all">{r.name}</Code></Td>
                        <Td>
                            <Code fontSize="xs" wordBreak="break-all">{r.value}</Code>
                            {r.purpose && (
                                <Text fontSize="11px" color="gray.500" mt={1}>
                                    {r.purpose === 'routing' && 'Routes traffic for your domain through Cloudflare.'}
                                    {r.purpose === 'ownership' && 'Proves you own this domain (TXT).'}
                                    {r.purpose === 'ssl-dcv' && 'Required for Cloudflare to issue your SSL certificate.'}
                                </Text>
                            )}
                        </Td>
                        <Td><IconButton aria-label="Copy" size="xs" variant="ghost" icon={<FiCopy />} onClick={() => onCopy(r.value)} /></Td>
                    </Tr>
                ))}
            </Tbody>
        </Table>
    );
}

export default function CustomDomainPanel() {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [row, setRow] = useState(null);
    const [hostname, setHostname] = useState('');
    const [adding, setAdding] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [activating, setActivating] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api('GET', '/domains');
            setRow(r);
        } catch (e) {
            if (e.status !== 503) toast({ status: 'error', title: 'Load failed', description: e.message });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => { load(); }, [load]);

    const copy = (text) => {
        navigator.clipboard.writeText(text).then(
            () => toast({ status: 'success', title: 'Copied', duration: 1500 }),
            () => toast({ status: 'error', title: 'Copy failed' }),
        );
    };

    const onAdd = async () => {
        const h = hostname.trim().toLowerCase();
        if (!HOSTNAME_RE.test(h)) {
            toast({ status: 'warning', title: 'Invalid hostname', description: 'Use something like go.yourdomain.com' });
            return;
        }
        setAdding(true);
        try {
            const r = await api('POST', '/domains', { hostname: h });
            setRow(r);
            setHostname('');
            toast({
                status: 'success',
                title: 'Custom domain added',
                description: 'Publish the DNS records below at your DNS provider, then click Refresh.',
                duration: 6000,
            });
        } catch (e) {
            if (e.status === 503) {
                toast({
                    status: 'error',
                    title: 'Cloudflare not configured',
                    description: 'CLOUDFLARE_API_TOKEN + CF_ZONE_ID_LINKS must be set on the Smart Links API.',
                    duration: 8000,
                });
            } else {
                toast({ status: 'error', title: 'Add failed', description: e.message });
            }
        } finally { setAdding(false); }
    };

    const onRefresh = async () => {
        if (!row?.cfHostnameId) { load(); return; }
        setRefreshing(true);
        try {
            const fresh = await api('GET', `/domains/${row.cfHostnameId}/status`);
            // Merge the live CF snapshot back into our row shape
            setRow((prev) => prev && { ...prev, sslStatus: fresh.sslStatus, cfSnapshot: fresh });
            if (fresh.isActive && row.status !== 'active') {
                toast({ status: 'info', title: 'DNS + SSL ready', description: 'Click Activate to wire it in.' });
            } else if (!fresh.isActive) {
                toast({ status: 'info', title: 'Still verifying', description: `CF status: ${fresh.status}, SSL: ${fresh.sslStatus || 'pending'}` });
            }
        } catch (e) {
            toast({ status: 'error', title: 'Refresh failed', description: e.message });
        } finally { setRefreshing(false); }
    };

    const onActivate = async () => {
        if (!row?.cfHostnameId || !row?.hostname) return;
        setActivating(true);
        try {
            await api('POST', `/domains/${row.cfHostnameId}/activate`, { hostname: row.hostname });
            await load();
            toast({ status: 'success', title: 'Custom domain active' });
        } catch (e) {
            toast({ status: 'error', title: 'Activate failed', description: e.message, duration: 6000 });
        } finally { setActivating(false); }
    };

    const onRemove = async () => {
        if (!row?.cfHostnameId) return;
        if (!window.confirm(`Remove custom domain ${row.hostname}? Existing links will revert to the canonical ${BRAND_NAME} URL.`)) return;
        try {
            await api('DELETE', `/domains/${row.cfHostnameId}`, { hostname: row.hostname });
            setRow(null);
            toast({ status: 'success', title: 'Custom domain removed' });
        } catch (e) {
            toast({ status: 'error', title: 'Remove failed', description: e.message });
        }
    };

    // domains.js returns dnsRecords + status at the TOP level (new shape after
    // the master-MySQL migration); cfSnapshot.* is the legacy path kept for
    // back-compat with any cached UI state.
    const records = row?.dnsRecords || row?.cfSnapshot?.dnsRecords;
    const cfReady = row?.isActive ?? row?.cfSnapshot?.isActive;

    if (loading) {
        return (
            <Flex justify="center" py={20}><Spinner size="lg" color="var(--brand-primary)" /></Flex>
        );
    }

    return (
        <Box>
            <Box mb={5}>
                <Heading size="lg">Custom Domain</Heading>
                <Text color="gray.500" fontSize="sm" mt={1}>
                    Serve your Smart Links on your own branded hostname. One custom domain per tenant; clicks stay on your domain end-to-end.
                </Text>
            </Box>

            {!row ? (
                <Box bg="white" borderWidth="1px" borderColor="gray.200" rounded="md" p={5}>
                    <Text fontSize="sm" fontWeight={600} color="gray.700" mb={2}>Add your Link</Text>
                    <HStack spacing={3} maxW="600px">
                        <Input
                            placeholder="go.yourdomain.com"
                            value={hostname}
                            onChange={(e) => setHostname(e.target.value)}
                            bg="white"
                            onKeyDown={(e) => e.key === 'Enter' && onAdd()}
                        />
                        <Button leftIcon={<FiPlus />} onClick={onAdd} isLoading={adding}>
                            Add domain
                        </Button>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={3}>
                        Use a subdomain you own (e.g. <Code fontSize="xs">go.yourdomain.com</Code>, not the apex <Code fontSize="xs">yourdomain.com</Code>).
                    </Text>
                </Box>
            ) : (
                <VStack align="stretch" spacing={5}>
                    <Box bg="white" borderWidth="1px" borderColor="gray.200" rounded="md" p={5}>
                        <Flex align="center" gap={3} wrap="wrap">
                            <Box>
                                <Text fontSize="11px" fontWeight={600} color="gray.500" letterSpacing="0.04em" textTransform="uppercase">Hostname</Text>
                                <HStack mt={1}>
                                    <Text fontFamily="mono" fontSize="md" color="gray.800">{row.hostname}</Text>
                                    <StatusPill status={row.status} sslStatus={row.sslStatus} />
                                </HStack>
                            </Box>
                            <Box flex={1} />
                            <Button leftIcon={<FiRefreshCw />} size="sm" variant="outline" onClick={onRefresh} isLoading={refreshing}>
                                Check status
                            </Button>
                            {row.status !== 'active' && cfReady && (
                                <Button leftIcon={<FiCheckCircle />} size="sm" colorScheme="green" onClick={onActivate} isLoading={activating}>
                                    Activate
                                </Button>
                            )}
                            <Button leftIcon={<FiTrash2 />} size="sm" colorScheme="red" variant="outline" onClick={onRemove}>
                                Remove
                            </Button>
                        </Flex>

                        {row.status === 'active' ? (
                            <Alert status="success" mt={4} rounded="md">
                                <AlertIcon />
                                <Box>
                                    <Text fontSize="sm" fontWeight={600}>Active</Text>
                                    <Text fontSize="xs" color="gray.700">
                                        New links will mint as <Code fontSize="xs">https://{row.hostname}/{'{code}'}</Code>. Existing links already display this domain in their shortUrl.
                                    </Text>
                                </Box>
                            </Alert>
                        ) : (
                            <Alert status="info" mt={4} rounded="md">
                                <AlertIcon />
                                <Box>
                                    <Text fontSize="sm" fontWeight={600}>Publish the DNS records below at your DNS provider.</Text>
                                    <Text fontSize="xs" color="gray.700">
                                        Cloudflare validates them automatically (usually within a few minutes). Click <em>Check status</em> until you see the green Activate button.
                                    </Text>
                                </Box>
                            </Alert>
                        )}
                    </Box>

                    <Box bg="white" borderWidth="1px" borderColor="gray.200" rounded="md" p={5}>
                        <Flex align="center" mb={4}>
                            <Box>
                                <Heading size="sm">DNS records to publish</Heading>
                                <Text fontSize="xs" color="gray.500">Add these at the DNS provider that controls {row.hostname.split('.').slice(-2).join('.')}.</Text>
                            </Box>
                            <Box flex={1} />
                            {records?.length > 0 && (
                                <IconButton aria-label="Refresh DNS records" size="sm" variant="ghost" icon={<FiRefreshCw />} onClick={onRefresh} isLoading={refreshing} />
                            )}
                        </Flex>
                        <DnsRecordsTable records={records} onCopy={copy} />
                    </Box>

                    {row.status === 'verifying' && (
                        <Alert status="warning" rounded="md">
                            <AlertIcon as={FiAlertCircle} />
                            <Text fontSize="sm">
                                If you've already published the records and verification still hasn't passed after ~10 minutes, double-check the CNAME points to the exact value above.
                            </Text>
                        </Alert>
                    )}
                </VStack>
            )}
        </Box>
    );
}
